import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isNoindexPath } from '../../data/noindexPaths';

/**
 * MetaDeduper
 *
 * public/index.html ships static SEO tags (description, canonical, og:*,
 * twitter:*) so non-JS crawlers always get valid meta. On JS-capable clients,
 * react-helmet-async APPENDS its own page-specific copies of the same tags -
 * leaving TWO of each in the <head> (the "duplicate meta description" +
 * duplicate/conflicting canonical flagged in SEO audits).
 *
 * This component consolidates each managed tag down to ONE node WITHOUT ever
 * producing zero:
 *   - keeps the persistent static node (the FIRST in DOM order),
 *   - copies the page-specific value (the LAST non-empty node, i.e. Helmet's)
 *     into that static node,
 *   - removes the remaining duplicates.
 *
 * Because the static node is never removed, any route - even one that sets no
 * meta of its own - always retains a valid tag. Runs after each navigation
 * with a few delayed passes to catch Helmet's async commit on lazy routes.
 */

// { selector, attr }. attr is the value-bearing attribute to consolidate.
const MANAGED = [
  { selector: 'meta[name="description"]', attr: 'content' },
  { selector: 'meta[name="title"]', attr: 'content' },
  { selector: 'meta[name="keywords"]', attr: 'content' },
  { selector: 'meta[name="robots"]', attr: 'content' },
  { selector: 'meta[property="og:type"]', attr: 'content' },
  { selector: 'meta[property="og:url"]', attr: 'content' },
  { selector: 'meta[property="og:title"]', attr: 'content' },
  { selector: 'meta[property="og:description"]', attr: 'content' },
  { selector: 'meta[property="og:image"]', attr: 'content' },
  { selector: 'meta[property="og:image:width"]', attr: 'content' },
  { selector: 'meta[property="og:image:height"]', attr: 'content' },
  { selector: 'meta[property="og:site_name"]', attr: 'content' },
  { selector: 'meta[property="og:locale"]', attr: 'content' },
  { selector: 'meta[name="twitter:card"]', attr: 'content' },
  { selector: 'meta[name="twitter:url"]', attr: 'content' },
  { selector: 'meta[name="twitter:title"]', attr: 'content' },
  { selector: 'meta[name="twitter:description"]', attr: 'content' },
  { selector: 'meta[name="twitter:image"]', attr: 'content' },
  { selector: 'link[rel="canonical"]', attr: 'href' },
];

function dedupeOnce() {
  if (typeof document === 'undefined') return;
  for (const { selector, attr } of MANAGED) {
    const nodes = Array.from(document.head.querySelectorAll(selector));
    if (nodes.length <= 1) continue;

    // The value to keep = the last node carrying a non-empty value (Helmet
    // appends last, so this is the page-specific value). Fall back to any
    // non-empty value if the last is blank.
    let value = '';
    for (let i = nodes.length - 1; i >= 0; i--) {
      const v = nodes[i].getAttribute(attr);
      if (v && v.trim()) { value = v; break; }
    }

    const keep = nodes[0]; // persistent static node stays in the DOM
    if (value) keep.setAttribute(attr, value);
    for (let i = 1; i < nodes.length; i++) {
      const el = nodes[i];
      if (el.parentNode) el.parentNode.removeChild(el);
    }
  }

  // Authoritative noindex for the thin programmatic long tail pruned after the
  // Sep-2026 spam update (src/data/noindexPaths.json). Runs after Helmet/dedupe
  // so it always wins, regardless of any per-page robots value.
  try {
    if (isNoindexPath(window.location.pathname)) {
      for (const name of ['robots', 'googlebot']) {
        let el = document.head.querySelector(`meta[name="${name}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute('name', name);
          document.head.appendChild(el);
        }
        el.setAttribute('content', 'noindex, follow');
      }
    }
  } catch { /* no-op */ }
}

const MetaDeduper = () => {
  const location = useLocation();
  useEffect(() => {
    // Multiple passes catch Helmet's async head commit and lazy-route hydration.
    const timers = [0, 250, 700, 1500].map((ms) => setTimeout(dedupeOnce, ms));
    const raf = typeof requestAnimationFrame !== 'undefined'
      ? requestAnimationFrame(dedupeOnce)
      : null;
    return () => {
      timers.forEach(clearTimeout);
      if (raf && typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(raf);
    };
  }, [location.pathname, location.search]);
  return null;
};

export default MetaDeduper;
